import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Building2, MapPin, Globe, Briefcase, ArrowLeft } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import SEO from "@/components/SEO";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import JobCard from "@/components/JobCard";
import VerifiedBadge from "@/components/VerifiedBadge";

export default function CompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/companies/${id}`).then((r) => setC(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-32 text-center">
          <h1 className="text-2xl font-bold text-[#1f222d]">Şirket bulunamadı</h1>
          <Link to="/sirketler" className="text-blue-600 font-semibold mt-4 inline-block">Şirketlere dön</Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!c) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="min-h-[50vh] flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: c.company_name,
    description: c.about,
    ...(c.website ? { url: c.website } : {}),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`${c.company_name} - Açık Pozisyonlar | isbul.org`}
        description={(c.about || `${c.company_name} şirketinin açık iş ilanları ve kurumsal bilgileri.`).slice(0, 160)}
        keywords={`${c.company_name}, ${c.sector}, iş ilanları, kariyer`}
        canonical={`/sirket/${c.id}`}
        jsonLd={jsonLd}
      />
      <Header />

      <div className="max-w-5xl mx-auto px-4 lg:px-8 py-8">
        <button data-testid="company-back" onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-slate-500 hover:text-[#1f222d] text-sm mb-6">
          <ArrowLeft size={16} /> Geri
        </button>

        <div className="bg-white border border-slate-200 rounded-2xl p-6 lg:p-8">
          <div className="flex items-start gap-5 flex-wrap">
            <div className="w-24 h-24 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center overflow-hidden shrink-0">
              {c.logo_file_id ? <img src={fileUrl(c.logo_file_id)} alt={c.company_name} className="w-full h-full object-contain" /> : <Building2 size={36} className="text-blue-600" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl sm:text-3xl font-extrabold text-[#1f222d]">{c.company_name}</h1>
                {c.verified && <VerifiedBadge showText />}
              </div>
              <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-600">
                {c.sector && <span className="inline-flex items-center gap-1.5"><Briefcase size={15} className="text-blue-600" /> {c.sector}</span>}
                {c.location && <span className="inline-flex items-center gap-1.5"><MapPin size={15} className="text-blue-600" /> {c.location}</span>}
                {c.website && <a href={c.website} target="_blank" rel="noreferrer" data-testid="company-website" className="inline-flex items-center gap-1.5 text-blue-600 hover:underline"><Globe size={15} /> {c.website.replace(/^https?:\/\//, "")}</a>}
              </div>
            </div>
          </div>

          {c.about && (
            <div className="mt-6 pt-6 border-t border-slate-100">
              <h2 className="font-semibold text-[#1f222d] mb-2">Hakkında</h2>
              <p className="text-slate-700 leading-relaxed whitespace-pre-line" data-testid="company-bio">{c.about}</p>
            </div>
          )}
        </div>

        <div className="mt-8">
          <h2 className="text-xl font-bold text-[#1f222d] mb-4">Açık Pozisyonlar ({c.job_count})</h2>
          {c.jobs.length === 0 ? (
            <p className="text-slate-500 bg-slate-50 rounded-2xl border border-slate-100 p-8 text-center">Bu şirketin şu anda açık ilanı yok.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5">
              {c.jobs.map((j) => <JobCard key={j.id} job={j} />)}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
